import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, HardHat, Ban, Pencil, ImagePlus } from 'lucide-react';
import { EMPLOYEE_SKILLS, type Employee, type EmployeeRole, type EmployeeLevel } from '@conversia/shared-types';
import * as employeesApi from '@/api/employees.api';
import * as usersApi from '@/api/users.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { employeeRoleLabel } from '@/lib/badges';
import { useToast } from '@/hooks/useToast';
import { Cargando } from '@/components/common/Cargando';

const rolOptions = [
  { value: 'TECNICO', label: 'Técnico' },
  { value: 'SOPORTE', label: 'Soporte' },
  { value: 'ADMIN', label: 'Administrador' },
];

const rolVariant: Record<EmployeeRole, 'brand' | 'info' | 'default'> = {
  ADMIN: 'brand',
  TECNICO: 'info',
  SOPORTE: 'default',
};

const skillLabel: Record<string, string> = {
  vendedor: 'Vendedor',
  mensajero: 'Mensajero',
  despacho: 'Despacho',
  caja: 'Caja',
  mayoreo: 'Ventas por mayor',
};

const nivelOptions = [
  { value: '', label: 'Sin calificar' },
  { value: 'A', label: 'Nivel A' },
  { value: 'B', label: 'Nivel B' },
  { value: 'C', label: 'Nivel C' },
];

const employeeSchema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio').max(120),
  telefono: z.string().max(40).optional(),
  rol: z.enum(['ADMIN', 'TECNICO', 'SOPORTE']),
});
type EmployeeValues = z.infer<typeof employeeSchema>;

function EmployeeFormModal({
  open,
  onClose,
  employee,
}: {
  open: boolean;
  onClose: () => void;
  employee: Employee | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [habilidades, setHabilidades] = useState<string[]>([]);
  const [nivel, setNivel] = useState<string>('');
  const [linkedUserId, setLinkedUserId] = useState<string>('');

  const isEdit = !!employee;

  // Sincroniza habilidades/nivel/cuenta (fuera de react-hook-form) al abrir/cambiar.
  useEffect(() => {
    setHabilidades(employee?.habilidades ?? []);
    setNivel(employee?.nivel ?? '');
    setLinkedUserId(employee?.userId ?? '');
  }, [employee]);

  // Cuenta de acceso: candidatos = usuarios sin ficha de empleado (más la cuenta
  // ya vinculada a ESTE empleado). Cierra el hueco de que el vínculo cuenta↔ficha
  // solo se podía hacer tocando la base de datos.
  const { data: usersData } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.listUsers({ take: 100 }),
    enabled: open,
  });
  const { data: employeesData } = useQuery({
    queryKey: ['employees', 'list'],
    queryFn: () => employeesApi.listEmployees({ take: 100 }),
    enabled: open,
  });
  const linkedElsewhere = new Set(
    (employeesData?.items ?? [])
      .filter((e) => e.id !== employee?.id && e.userId)
      .map((e) => e.userId as string),
  );
  const userOptions = [
    { value: '', label: 'Sin acceso al panel' },
    ...(usersData?.items ?? [])
      .filter((u) => u.isActive && !linkedElsewhere.has(u.id))
      .map((u) => ({
        value: u.id,
        label: `${u.name ?? u.email}${u.name ? ` (${u.email})` : ''}`,
      })),
  ];

  function toggleSkill(skill: string) {
    setHabilidades((cur) => (cur.includes(skill) ? cur.filter((s) => s !== skill) : [...cur, skill]));
  }
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<EmployeeValues>({
    resolver: zodResolver(employeeSchema),
    values: {
      nombre: employee?.nombre ?? '',
      telefono: employee?.telefono ?? '',
      rol: employee?.rol ?? 'TECNICO',
    },
  });

  function close() {
    setPhoto(null);
    setPreview(null);
    reset();
    onClose();
  }

  const save = useMutation({
    mutationFn: async (values: EmployeeValues) => {
      const payload = {
        ...values,
        telefono: values.telefono?.trim() || undefined,
        habilidades,
        nivel: (nivel || null) as EmployeeLevel | null,
      };
      const saved = isEdit
        ? await employeesApi.updateEmployee(employee.id, {
            ...payload,
            // null desvincula la cuenta; un id la vincula.
            userId: linkedUserId || null,
          })
        : await employeesApi.createEmployee({
            ...payload,
            userId: linkedUserId || undefined,
          });
      if (photo) await employeesApi.uploadEmployeePhoto(saved.id, photo);
      return saved;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      toast.success(isEdit ? 'Empleado actualizado' : 'Empleado creado');
      close();
    },
    onError: (err) =>
      setError('root', {
        message: err instanceof ApiError ? err.message : 'No se pudo guardar el empleado',
      }),
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPhoto(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  }

  const onSubmit = handleSubmit((values) => save.mutate(values));

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? 'Editar empleado' : 'Nuevo empleado'}
      description="Perfil del equipo de operaciones (técnicos, soporte, admin)."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close}>
            Cancelar
          </Button>
          <Button data-guia="employee-form.guardar" onClick={onSubmit} loading={save.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear empleado'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex items-center gap-4">
          <Avatar name={employee?.nombre} src={preview ?? employee?.fotoUrl} size="lg" />
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={onPickFile}
            />
            <Button variant="ghost" size="sm" type="button" onClick={() => fileRef.current?.click()}>
              <ImagePlus className="h-4 w-4" /> {photo ? 'Cambiar foto' : 'Subir foto'}
            </Button>
            {photo && <p className="mt-1 truncate text-xs text-slate-400">{photo.name}</p>}
          </div>
        </div>
        <Input
          label="Nombre"
          placeholder="Nombre y apellido"
          error={errors.nombre?.message}
          {...register('nombre')}
        />
        <Input data-guia="employee-form.telefono" label="Teléfono" placeholder="809-555-0000" error={errors.telefono?.message} {...register('telefono')} />
        <Select label="Rol" options={rolOptions} {...register('rol')} />
        <Select
          label="Nivel"
          options={nivelOptions}
          value={nivel}
          onChange={(e) => setNivel(e.target.value)}
        />
        <div>
          <Select
            label="Cuenta de acceso"
            options={userOptions}
            value={linkedUserId}
            onChange={(e) => setLinkedUserId(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-400">
            Con qué usuario entra al panel. Un técnico vinculado ve SOLO sus trabajos en
            su teléfono. Cree la cuenta primero en Usuarios (rol Técnico) si no aparece.
          </p>
        </div>
        <fieldset className="space-y-2">
          <legend className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Habilidades</legend>
          <div className="flex flex-wrap gap-3">
            {EMPLOYEE_SKILLS.map((skill) => (
              <label key={skill} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={habilidades.includes(skill)}
                  onChange={() => toggleSkill(skill)}
                />
                {skillLabel[skill] ?? skill}
              </label>
            ))}
          </div>
        </fieldset>
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}

export default function Employees() {
  const qc = useQueryClient();
  const toast = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['employees', 'list'],
    queryFn: () => employeesApi.listEmployees({ take: 100 }),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => employeesApi.deactivateEmployee(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      toast.info('Empleado desactivado');
    },
    onError: () => toast.error('No se pudo desactivar'),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => employeesApi.updateEmployee(id, { activo: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Empleado reactivado');
    },
    onError: () => toast.error('No se pudo reactivar'),
  });

  const items = data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(employee: Employee) {
    setEditing(employee);
    setFormOpen(true);
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Empleados"
        helpKey="employees"
        description="Equipo de operaciones: técnicos, soporte y administradores."
        action={
          <Button data-guia="employees.nuevo" onClick={openCreate}>
            <UserPlus className="h-4 w-4" /> Nuevo empleado
          </Button>
        }
      />

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <Cargando />
          ) : items.length === 0 ? (
            <EmptyState
              icon={HardHat}
              title="Sin empleados"
              description="Cree la ficha de cada técnico (con su teléfono) para poder asignarle trabajos."
              className="m-5"
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {items.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <Avatar name={e.nombre} src={e.fotoUrl} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{e.nombre}</p>
                    <p className="truncate text-xs text-slate-400">{e.telefono ?? '—'}</p>
                    {e.habilidades.length > 0 && (
                      <p className="mt-0.5 truncate text-xs text-slate-400">
                        {e.habilidades.map((s) => skillLabel[s] ?? s).join(' · ')}
                      </p>
                    )}
                  </div>
                  {e.nivel && <Badge variant="default">Nivel {e.nivel}</Badge>}
                  <Badge variant={rolVariant[e.rol]}>{employeeRoleLabel[e.rol]}</Badge>
                  {!e.activo && <Badge variant="danger">Inactivo</Badge>}
                  <Button
                    variant="ghost"
                    size="sm"
                    data-guia="employees.fila.editar"
                    aria-label={`Editar a ${e.nombre}`}
                    onClick={() => openEdit(e)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {e.activo ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Desactivar empleado"
                      onClick={() => deactivate.mutate(e.id)}
                      loading={deactivate.isPending && deactivate.variables === e.id}
                    >
                      <Ban className="h-4 w-4 text-danger" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reactivate.mutate(e.id)}
                      loading={reactivate.isPending && reactivate.variables === e.id}
                    >
                      Reactivar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <EmployeeFormModal open={formOpen} onClose={() => setFormOpen(false)} employee={editing} />
    </div>
  );
}
